/**
 * 后端 API 客户端
 * 浏览器 → EdgeOne Pages /api/* → CVM
 * JWT 通过 Authorization: Bearer 头自动带上
 */
import { useAuth } from './store'
import { streamAgent as sseStreamAgent } from './sse'
import { track as trackerTrack } from './tracker'

const TOKEN_KEY = 'gaokao_token'

/**
 * 前后端统一每页大小。
 * 后端对应:gaokao-web-edge/cloud-functions/routes/{school,stats}/queries.ts 里的 `const PAGE = 20`
 * 改这里必须同步改后端,否则 Pager 显示页数 / 实际返回行数对不上。
 */
export const PAGE_SIZE = 20

export class ApiError extends Error {
  status: number
  code: string
  body: any
  constructor(status: number, code: string, body: any) {
    super(`API ${status} ${code}`)
    this.status = status
    this.code = code
    this.body = body
  }
}

/**
 * 干净 URLSearchParams:过滤 undefined / null / 空串 / NaN
 * 修 bug:`new URLSearchParams({x: undefined})` 会输出 "x=undefined" 而不是跳过,
 * 后端 Zod coerce 会把字符串 "undefined" 转成 NaN 然后校验失败。
 */
export function cleanParams(q: Record<string, unknown>): URLSearchParams {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue
    if (typeof v === 'number' && Number.isNaN(v)) continue
    usp.set(k, String(v))
  }
  return usp
}

async function call<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (init.auth !== false) {
    const t = useAuth.getState().token ?? localStorage.getItem(TOKEN_KEY)
    if (t) headers.set('Authorization', `Bearer ${t}`)
  }
  let res: Response
  try {
    res = await fetch(`/api${path}`, { ...init, headers })
  } catch (e) {
    // 网络断 / DNS 挂 / CORS / 代理拦截 → fetch 抛 TypeError
    throw new ApiError(0, 'network_error', { message: (e as Error).message })
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body?.error || `http_${res.status}`, body)
  }
  return (await res.json()) as T
}

// ---- 通用 ----
export function get<T = any>(path: string): Promise<T> {
  return call<T>(path, { method: 'GET' })
}
export function post<T = any>(path: string, body?: any, auth = true): Promise<T> {
  return call<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined, auth })
}

// ---- 认证 ----
export interface LoginResponse {
  ok: true
  token: string
  user: { id: number; phone: string; nickname: string | null; isAdmin: boolean }
}

export function loginPassword(phone: string, password: string): Promise<LoginResponse> {
  return call<LoginResponse>('/auth/login-password', {
    method: 'POST',
    body: JSON.stringify({ phone, password }),
    auth: false,
  })
}

export function getConfig(): Promise<{ cvm_url: string; cvm_token: string }> {
  return call('/config', { auth: false })
}

// ---- 模块 1:学校查询 ----
export const school = {
  scores: (q: { school_id?: number; school_name?: string; year?: number; curriculum?: string; offset?: number }) =>
    get('/school/scores?' + new URLSearchParams(q as any)),
  majors: (q: { school_id: number; year?: number; offset?: number }) =>
    get('/school/majors?' + new URLSearchParams(q as any)),
  majorsByName: (q: { major_name: string; year?: number; offset?: number }) =>
    get('/school/majors-by-name?' + new URLSearchParams(q as any)),
  majorGroups: (q: { year?: number; curriculum?: string; simplify_special_course?: string; offset?: number }) =>
    get('/school/major-groups?' + new URLSearchParams(q as any)),
  list: (q: { year?: number; curriculum?: string; batch_name?: string; offset?: number }) =>
    get('/school/list?' + new URLSearchParams(q as any)),
  enrollmentTrend: (q: { school_id?: number; school_name?: string; major_name: string; curriculum?: string }) =>
    get('/school/enrollment-trend?' + new URLSearchParams(q as any)),
  minScoreTrend: (q: { school_id?: number; school_name?: string; major_name: string; curriculum?: string }) =>
    get('/school/min-score-trend?' + new URLSearchParams(q as any)),
  // 排行榜(按软科 / 保研 / 学费)
  // 后端强制 PAGE=20(不能传 limit),只接受 offset 翻页
  top: (q: { by?: 'ranking_ruanke' | 'baoyan_rate' | 'tuition_min' | 'tuition_max'; order?: 'asc' | 'desc'; offset?: number; is_985?: number; is_211?: number; is_double_first?: number; school_type?: string } = {}) =>
    get('/school/top?' + cleanParams(q as any).toString()),
  // 多维筛选(学费预算 + 985/211/双一流 + 类型 + 省份)
  filter: (q: { tuition_min?: number; tuition_max?: number; is_985?: number; is_211?: number; is_double_first?: number; school_type?: string; province?: string; offset?: number } = {}) =>
    get('/school/filter?' + cleanParams(q as any).toString()),
  // 招生章程字段筛选(色盲 / 英语单科 / 口试 / 语种 / 性别 / 中外合作)
  byRules: (q: { english_min?: number; color_blind?: 'only' | 'exclude'; sino_foreign?: number; oral_exam?: number; language?: string; gender_limit?: string; offset?: number } = {}) =>
    get('/school/by-rules?' + cleanParams(q as any).toString()),
  // 按位次查学校专业
  byRank: (q: { rank: number; year?: number; curriculum?: string; tolerance?: number; batch_name?: string; offset?: number }) =>
    get('/school/by-rank?' + cleanParams(q as any).toString()),
}

// ---- 模块 2:统计 ----
export const stats = {
  leaderboard: (q: { year?: number; curriculum?: string; batch_name?: string; sort_by?: string; order?: string; offset?: number }) =>
    get('/stats/leaderboard?' + new URLSearchParams(q as any)),
  hotMajors: (q: { year?: number; curriculum?: string; offset?: number }) =>
    get('/stats/hot-majors?' + new URLSearchParams(q as any)),
  enrollmentChange: (q: { year_current?: number; year_prev?: number; curriculum?: string; offset?: number }) =>
    get('/stats/enrollment-change?' + new URLSearchParams(q as any)),
  sizeYearMarks: (q: { year?: number; curriculum?: string; threshold?: number; offset?: number }) =>
    get('/stats/size-year-marks?' + new URLSearchParams(q as any)),
  scoreDistribution: (q: { year?: number; curriculum?: string; bucket?: number }) =>
    get('/stats/score-distribution?' + new URLSearchParams(q as any)),
}

// ---- 模块 3:Agent (SSE) ----
export type CityTier = 'tier1' | 'newtier1' | 'tier2' | 'unlimited'
export type DistancePref = 'home_province' | 'neighbor' | 'must_tier1' | 'must_south' | 'must_north' | 'unlimited'
export type SchoolType = 'public' | 'private' | 'sino_foreign' | 'independent' | 'remote_campus'
export type TuitionBudget = 'unlimited' | 'under_5k' | '5k_15k' | '15k_30k' | 'over_30k'
export type CareerDirection = 'job' | 'graduate_school' | 'abroad' | 'civil_service' | 'entrepreneur' | 'undecided'
export type MajorHotPref = 'hot' | 'moderate' | 'cold' | 'unlimited'
export type BaoyanIntent = 'must' | 'prefer' | 'no_preference' | 'not_needed'
export type Adjustment = 'comply' | 'no_comply' | 'depends'
export type BodyLimit = 'vision' | 'color_blind' | 'height' | 'hearing'
export type StrongSubject = 'math' | 'physics' | 'chemistry' | 'biology' | 'history' | 'politics' | 'geography' | 'english' | 'chinese'

export interface AgentInput {
  // 基础
  score: number
  rank: number
  curriculum: '物理类' | '历史类'
  subject: string
  gender?: 'male' | 'female'
  // 地理
  city_tier?: CityTier
  distance?: DistancePref
  // 其他
  requirements?: string
  // 院校
  school_types?: SchoolType[]
  tuition?: TuitionBudget
  // 专业
  prefer_profession?: string
  strong_subjects?: StrongSubject[]
  target_job?: string
  career?: CareerDirection
  major_hot_pref?: MajorHotPref
  baoyan?: BaoyanIntent
  // 策略
  risk?: 'aggressive' | 'balanced' | 'conservative'
  adjustment?: Adjustment
  // 身体(高级)
  body_limits?: BodyLimit[]
  min_math?: number
  min_english?: number
  min_chinese?: number
}

/** Agent 报告:LLM 输出的 markdown 全文 + 校验警告 */
export interface AgentReport {
  generatedAt: string
  model: string
  input: AgentInput
  /** 完整 markdown 报告(LLM 流式输出后累积) */
  markdown: string
  /** 校名校验失败的校名列表 */
  warnings: string[]
}

export type AgentSseEvent =
  | { type: 'progress'; step: number; total: number; message: string }
  | { type: 'tool'; tool: string; args: unknown }
  | { type: 'token'; text: string }
  | { type: 'agent_msg'; turn: number; text: string }
  | { type: 'report'; report: AgentReport }
  | { type: 'done' }
  | { type: 'error'; error: string }

export async function streamAgent(
  input: AgentInput,
  onEvent: (ev: AgentSseEvent) => void,
): Promise<AgentReport | null> {
  let report: AgentReport | null = null
  for await (const ev of sseStreamAgent(input)) {
    const e = ev as AgentSseEvent
    onEvent(e)
    if (e.type === 'report') report = e.report
  }
  return report
}

// ---- 模块 4:事件 + admin ----
/** 浏览器端事件埋点 — 入队后由 tracker 聚合 + sendBeacon 提交,API 保持原签名,调用点不变 */
export function trackEvent(body: any): Promise<{ ok: true }> {
  return trackerTrack(body)
}

// admin 事件流看板(MODULE4 开关已删除,events 始终记录)
export interface AdminAnalytics {
  events_count: number
  by_type: { search_school: number; view_school: number; view_major: number; report_include: number }
  last_24h: number
  last_7d_daily: Array<{ d: number; n: number }>
  top_schools_7d: Array<{ name: string; n: number }>
  top_majors_7d: Array<{ name: string; n: number }>
}
export function getAdminAnalytics(token: string): Promise<{ ok: true } & AdminAnalytics> {
  return call('/admin/analytics', { headers: { Authorization: `Bearer ${token}` } })
}

// ---- admin 用户管理(CRUD) ----
export interface AdminUser {
  id: number
  phone: string
  nickname: string | null
  is_admin: number   // 0 / 1
  status: string     // 'active' | 'banned' | 'deleted'
  created_at: string
  last_login_at: string | null
}
export interface ListAdminUsersResp {
  ok: true
  users: AdminUser[]
  total: number
}
export function listAdminUsers(q: { page?: number; limit?: number; q?: string } = {}): Promise<ListAdminUsersResp> {
  const usp = new URLSearchParams()
  if (q.page) usp.set('page', String(q.page))
  if (q.limit) usp.set('limit', String(q.limit))
  if (q.q) usp.set('q', q.q)
  return get('/admin/users?' + usp.toString())
}
export function createAdminUser(body: { phone: string; password: string; nickname?: string; isAdmin?: boolean }): Promise<{ ok: true; id: number }> {
  return post('/admin/users', body)
}
export function updateAdminUser(id: number, body: { nickname?: string | null; status?: 'active' | 'banned'; isAdmin?: boolean }): Promise<{ ok: true }> {
  return call(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
}
export function resetAdminPassword(id: number, password: string): Promise<{ ok: true }> {
  return post(`/admin/users/${id}/reset-password`, { password })
}
export function deleteAdminUser(id: number): Promise<{ ok: true }> {
  return call(`/admin/users/${id}`, { method: 'DELETE' })
}

// ---- 站点使用统计(公开) ----
export interface SiteStats {
  total_users: number
  total_events: number
  total_searches: number
  total_school_views: number
  total_major_views: number
  total_reports: number
  last_24h_searches: number  // 近 24h search_school 次数
  last_7d_reports: number    // 近 7d report_include 次数
}
export function getSiteStats(): Promise<{ ok: true } & SiteStats> {
  return call('/site')
}

// 实时热度(从 events 表聚合,近 24h 搜 / 7d 看专业 / reports by curriculum / 7d 每日桶)
export interface HotItem { name: string; n: number }
export interface ReportsByCurr { curriculum: string; n: number }
export interface SiteHot {
  hot_schools_24h: HotItem[]
  hot_majors_7d: HotItem[]
  reports_by_curriculum: ReportsByCurr[]
  last_7d_daily: number[]  // 7 个数,index 0 = 6 天前, 6 = 今天
}
export function getSiteHot(): Promise<{ ok: true } & SiteHot> {
  return call('/site/hot')
}

// 单校实时热度 — 学校详情页用
export interface SchoolHot {
  school_name: string
  counts_24h: number
  counts_7d: number
  daily_7d: number[]  // 7 个数,index 0=6 天前, index 6=今天
}
export function getSchoolHot(schoolName: string): Promise<{ ok: true } & SchoolHot> {
  return call('/site/school-hot?school_name=' + encodeURIComponent(schoolName))
}

// ---- 搜索 ----
export interface SearchResult {
  schools: { id: number; school_name: string }[]
  majors: { major_name: string; school_count: number }[]
}

export function search(q: string, limit = 8): Promise<{ ok: true } & SearchResult> {
  return call(`/search?q=${encodeURIComponent(q)}&limit=${limit}`, { method: 'GET', auth: true })
}

// ---- 报告历史(模块 3 用户 AI 报告) ----
export interface ReportSummary {
  id: number
  title: string
  created_at: string
}

export interface SavedReport {
  id: number
  title: string
  created_at: string
  report: AgentReport
}

export function listReports(): Promise<{ ok: true; reports: ReportSummary[] }> {
  return get('/reports')
}

export function getReport(id: number): Promise<{ ok: true; report: SavedReport }> {
  return get(`/reports/${id}`)
}

export function deleteReport(id: number): Promise<{ ok: true; deleted: number }> {
  return call(`/reports/${id}`, { method: 'DELETE' })
}

// ---- 预登记(未登录用户点"去申请账号") ----
export function createPreRegistration(input: Partial<AgentInput>): Promise<{ ok: true }> {
  return call('/pre-registration', {
    method: 'POST',
    body: JSON.stringify(input),
    auth: false,
  })
}

// ---- 错误码转中文 ----
export function translateError(e: ApiError | string): string {
  if (e instanceof ApiError) {
    // 如果有后端返回的详细错误信息,优先使用
    const detail = e.body?.detail
    if (detail?.msg) return detail.msg
    // 否则用错误码映射
    return translateCode(e.code)
  }
  return translateCode(e)
}
function translateCode(code: string): string {
  switch (code) {
    case 'user_not_found':
      return '这个手机号还没注册'
    case 'password_not_set':
      return '账号还没设密码,联系客服开通'
    case 'wrong_password':
      return '密码不对,再试试'
    case 'banned':
      return '账号被封了,联系客服了解情况'
    case 'invalid_body':
      return '请求格式不对,刷新重试'
    case 'pii_detected':
      return '检测到证件号,改用昵称,别输身份证'
    case 'module4_disabled':
      return '实时榜单暂未开放,6/25 后自动开'
    case 'no_token':
    case 'bad_token':
      return '登录失效了,重新登录'
    case 'not_admin':
      return '这个操作需要管理员账号'
    case 'phone_exists':
      return '这个手机号已经有账号了'
    case 'cannot_demote_self':
      return '不能把自己降级为非管理员'
    case 'cannot_delete_self':
      return '不能删除自己的账号'
    case 'no_changes':
      return '没有可更新的字段'
    case 'not_found':
      return '内容不存在或已删除'
    case 'corrupt_report':
      return '报告数据有问题,删了重生成'
    case 'score_rank_mismatch':
      return '你填的分数和位次对不上,请核实后再提交'
    case 'network_error':
      return '网络好像断了,刷新试试'
    default:
      return code
  }
}

